// =============================================================================
// MeuDriver — AdminPayoutsDueCard
// "Repasses a pagar": total devido por instrutor (aulas consumidas que já
// passaram da janela pós-aula), com a chave Pix. O admin faz o Pix pelo app do
// banco / Mercado Pago e marca como pago informando o identificador (E2E).
// Quando a API de Payouts do MP estiver liberada (secret MP_PAYOUTS_ENABLED),
// o cron `instructor-payout` envia sozinho e as linhas aparecem "em envio".
// =============================================================================

import { useState } from 'react'
import { format, parseISO } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { toast } from 'sonner'
import { Banknote, Copy } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { formatCurrency } from '@/lib/utils'
import { PIX_KEY_LABELS, useAdminMarkEarningsPaid, useAdminPayoutsDue, type PayoutDue } from '@/hooks/useEarnings'

export function AdminPayoutsDueCard() {
  const { data: rows, isLoading } = useAdminPayoutsDue()
  const total = (rows ?? []).reduce((s, r) => s + r.total, 0)

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <Banknote className="h-4 w-4 text-brand-600" />
          Repasses a pagar {rows && rows.length > 0 && <span className="text-muted-foreground font-normal">· {formatCurrency(total)}</span>}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-20 w-full" />
        ) : !rows || rows.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4">Nenhum repasse pendente.</p>
        ) : (
          <ul className="space-y-3">
            {rows.map((r) => <Linha key={r.instructor_id} row={r} />)}
          </ul>
        )}
      </CardContent>
    </Card>
  )
}

function Linha({ row }: { row: PayoutDue }) {
  const [ref, setRef] = useState('')
  const mark = useAdminMarkEarningsPaid()
  const pendentes = row.lessons - row.processing

  async function marcar() {
    try {
      const n = await mark.mutateAsync({ earningIds: row.earning_ids, reference: ref })
      toast.success(`${n} repasse(s) marcado(s) como pago(s).`)
      setRef('')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Erro.')
    }
  }

  return (
    <li className="rounded-xl border border-border/60 p-4 space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="font-semibold text-gray-900">{row.instructor_name ?? 'Instrutor'}</p>
          <p className="text-xs text-muted-foreground">
            {row.lessons} aula(s) · mais antiga liberada em {format(parseISO(row.oldest), "d 'de' MMM", { locale: ptBR })}
            {row.processing > 0 && ` · ${row.processing} em envio automático`}
          </p>
        </div>
        <p className="text-lg font-bold text-gray-900">{formatCurrency(row.total)}</p>
      </div>
      {row.pix_key ? (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-muted-foreground">{PIX_KEY_LABELS[row.pix_key_type!]}:</span>
          <strong>{row.pix_key}</strong>
          <span className="text-muted-foreground">({row.holder_name})</span>
          <Button type="button" size="sm" variant="ghost" className="h-7 px-2"
            onClick={() => { navigator.clipboard.writeText(row.pix_key!).then(() => toast.success('Chave copiada')) }}>
            <Copy className="h-3.5 w-3.5" />
          </Button>
        </div>
      ) : (
        <p className="text-sm text-amber-700">O instrutor ainda não cadastrou a chave Pix — não dá para pagar.</p>
      )}
      {row.pix_key && pendentes > 0 && (
        <div className="flex flex-col gap-2 sm:flex-row">
          <Input placeholder="Identificador do Pix enviado (E2E do comprovante)" value={ref} onChange={(e) => setRef(e.target.value)} />
          <Button type="button" disabled={mark.isPending || ref.trim().length < 3} onClick={marcar}>
            {mark.isPending ? 'Marcando...' : 'Marcar como pago'}
          </Button>
        </div>
      )}
    </li>
  )
}
